import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import CoachDashboardModern from './pages/CoachDashboardModern';
import Login from './pages/Login';
import ClientLogin from './pages/ClientLogin';
import ClientSignup from './pages/ClientSignup';
import PublicSignup from './pages/PublicSignup';
import ResetPassword from './pages/ResetPassword';
import ForgotPassword from './pages/ForgotPassword';
import VerifyEmail from './pages/VerifyEmail';
import Signup from './pages/Signup';
import SignupCheckout from './pages/SignupCheckout';
import SignupSuccess from './pages/SignupSuccess';
import MealPlanModern from './pages/MealPlanModern';
import ClientDashboardModern from './pages/ClientDashboardModern';
import PaymentSuccess from './pages/PaymentSuccess';
import FloatingChatBubble from './components/FloatingChatBubble';
import CoachFloatingChat from './components/CoachFloatingChat';
import { logActivity } from './lib/activityLog';
import CoachProfile from './pages/CoachProfile';
import AdminTools from './pages/AdminTools';
import WeeklyCheckin from './pages/WeeklyCheckin';
import WeightLog from './pages/WeightLog';
import Finance from './pages/Finance';
import FoodBank from './pages/FoodBank';
import RecipeLibrary from './pages/RecipeLibrary';
import TierRecipeLibrary from './pages/TierRecipeLibrary';
import BulkTagEditor from './pages/BulkTagEditor';
import RecipeEditor from './pages/RecipeEditor';
import AddRecipe from './pages/AddRecipe';
import AddRecipeBatch from './pages/AddRecipeBatch';
import FoodBankManager from './pages/FoodBankManager';
import ClientProgress from './pages/ClientProgress';
import OnboardingSwipeable from './pages/OnboardingSwipeable';
import OrientationIntro from './pages/OrientationIntro';
import Messages from './pages/Messages';
import Profile from './pages/Profile';
import WeekSummary from './pages/WeekSummary';
import ClientProfile from './pages/ClientProfile';
import ClientsList from './pages/ClientsList';
import TestCheckinManager from './pages/TestCheckinManager';
import PendingAdjustments from './pages/PendingAdjustments';
import HowItWorks from './pages/HowItWorks';
import CoachMessages from './pages/CoachMessages';
import RecipeReportsDashboard from './pages/RecipeReportsDashboard';
import RecipeAudit from './pages/RecipeAudit';
import CSVImport from './pages/CSVImport';
import FridayBatchView from './pages/FridayBatchView';
import WeeklyReviews from './pages/WeeklyReviews';
import LandingPage from './pages/LandingPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import RefundPolicy from './pages/RefundPolicy';
import FeedbackPage from './pages/FeedbackPage';
import AdminEmailUpdate from './pages/AdminEmailUpdate';
import CancellationFeedback from './pages/CancellationFeedback';
import Resubscribe from './pages/Resubscribe';
import SubscriptionGuard from './components/SubscriptionGuard';
import CookieConsentBanner from './components/CookieConsentBanner';
import { Toaster } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper functions for role-based token storage
const getTokenKey = (role) => role === 'coach' ? 'coach_token' : 'client_token';
const getUserKey = (role) => role === 'coach' ? 'coach_user' : 'client_user';

const getCurrentToken = () => {
  const path = window.location.pathname;
  
  // If on coach portal (/coach/*), use coach token
  if (path.startsWith('/coach')) {
    return localStorage.getItem('coach_token');
  }
  
  // If on client portal (/app/*), use client token
  if (path.startsWith('/app')) {
    return localStorage.getItem('client_token');
  }
  
  // Fallback: try coach first, then client
  return localStorage.getItem('coach_token') || localStorage.getItem('client_token');
};

// Axios interceptor for auth
axios.interceptors.request.use(
  (config) => {
    const token = getCurrentToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if this is a login request (don't redirect on login failures)
      const isLoginRequest = error.config?.url?.includes('/auth/login');
      
      if (!isLoginRequest) {
        // Clear only the relevant token based on the request
        const token = getCurrentToken();
        const path = window.location.pathname;
        
        if (token === localStorage.getItem('coach_token')) {
          localStorage.removeItem('coach_token');
          localStorage.removeItem('coach_user');
          // Only redirect if we're actually on a coach page
          if (path.startsWith('/coach') && !path.includes('/login')) {
            // Add a flag to show session expired message
            localStorage.setItem('session_expired', 'true');
            window.location.href = '/coach/login';
          }
        } else {
          localStorage.removeItem('client_token');
          localStorage.removeItem('client_user');
          // Only redirect if we're actually on a client page
          if (path.startsWith('/app') && !path.includes('/login')) {
            // Add a flag to show session expired message
            localStorage.setItem('session_expired', 'true');
            window.location.href = '/app/login';
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

// Component to track and restore last visited page
function NavigationPersistence({ isClient = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const storageKey = isClient ? 'client_last_path' : 'coach_last_path';

  // Save current path to localStorage whenever it changes
  useEffect(() => {
    // Don't save login/signup pages
    if (!location.pathname.includes('login') && 
        !location.pathname.includes('signup') &&
        !location.pathname.includes('forgot-password') &&
        !location.pathname.includes('reset-password')) {
      localStorage.setItem(storageKey, location.pathname);
    }
  }, [location.pathname, storageKey]);

  // Restore last path on mount (only once)
  useEffect(() => {
    const lastPath = localStorage.getItem(storageKey);
    const basePath = isClient ? '/app' : '/coach';
    
    // Only restore if we're on the base path and have a saved path
    if (location.pathname === basePath && lastPath && lastPath !== basePath) {
      // Small delay to ensure user is authenticated
      setTimeout(() => {
        navigate(lastPath, { replace: true });
      }, 100);
    }
  }, []); // Only run once on mount

  return null; // This component doesn't render anything
}

// Coach Portal Component
function CoachPortal() {
  const [coachUser, setCoachUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('coach_token');
    const savedUser = localStorage.getItem('coach_user');
    
    if (token && savedUser) {
      const user = JSON.parse(savedUser);
      if (user.role === 'coach') {
        // Validate token with a test API call
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/coach/dashboard`, { timeout: 8000 })
          .then(() => {
            // Token is valid
            setCoachUser(user);
            setLoading(false);
          })
          .catch(() => {
            // Token expired/invalid or server unreachable
            console.log('[CoachPortal] Session expired or server unreachable, clearing...');
            localStorage.removeItem('coach_token');
            localStorage.removeItem('coach_user');
            delete axios.defaults.headers.common['Authorization'];
            setLoading(false);
          });
        
        return; // Don't call setLoading(false) here
      }
    }
    setLoading(false);
  }, []);

  const handleCoachLogin = (userData, token) => {
    localStorage.setItem('coach_token', token);
    localStorage.setItem('coach_user', JSON.stringify(userData));
    setCoachUser(userData);
  };

  const handleCoachLogout = () => {
    localStorage.removeItem('coach_token');
    localStorage.removeItem('coach_user');
    setCoachUser(null);
  };

  if (loading) {
    return <div className="loading-screen">Loading Coach Portal...</div>;
  }

  return (
    <>
    <Routes>
      {/* Public admin route - accessible without login */}
      <Route path="/admin/update-email" element={<AdminEmailUpdate />} />
      
      <Route path="/login" element={!coachUser ? <Login onLogin={handleCoachLogin} /> : <Navigate to="/coach" />} />
      
      {coachUser ? (
        <>
          {/* Navigation Persistence - tracks and restores last visited page */}
          <Route path="*" element={<NavigationPersistence isClient={false} />} />
          
          <Route path="/" element={<CoachDashboardModern user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/coach" element={<CoachDashboardModern user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/clients" element={<ClientsList user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/client/:clientId" element={<ClientProfile user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/finance" element={<Finance user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/recipe-library" element={<TierRecipeLibrary user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/recipe-library-old" element={<RecipeLibrary user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/recipe-library/bulk-tag" element={<BulkTagEditor user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/recipe-library/:recipeId/edit" element={<RecipeEditor user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/add-recipe" element={<AddRecipe user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/add-recipe-batch" element={<AddRecipeBatch user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/food-bank" element={<FoodBank user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/food-bank/manage" element={<FoodBankManager user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/test-checkins" element={<TestCheckinManager user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/messages" element={<CoachMessages user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/profile" element={<CoachProfile user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/admin-tools" element={<AdminTools user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/recipe-reports" element={<RecipeReportsDashboard user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/recipe-audit" element={<RecipeAudit user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/csv-import" element={<CSVImport user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/friday-batch" element={<FridayBatchView user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/weekly-reviews" element={<WeeklyReviews user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="/cancellation-feedback" element={<CancellationFeedback user={coachUser} onLogout={handleCoachLogout} />} />
          <Route path="*" element={<Navigate to="/coach" />} />
        </>
      ) : (
        <Route path="*" element={<Navigate to="/login" />} />
      )}
    </Routes>
    {coachUser && <CoachFloatingChat coachUser={coachUser} />}
    </>
  );
}

// Client Portal Component
function ClientPortal() {
  const [clientUser, setClientUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('[ClientPortal] Init - checking for impersonation/auto-login...');
    console.log('[ClientPortal] Current URL:', window.location.href);
    console.log('[ClientPortal] Search params:', window.location.search);
    
    // Try multiple ways to get URL params (iOS Safari compatibility)
    let params = new URLSearchParams(window.location.search);
    
    // Fallback: parse from full URL if search is empty but URL contains params
    if (!params.has('imp_token') && window.location.href.includes('imp_token=')) {
      const urlParts = window.location.href.split('?');
      if (urlParts.length > 1) {
        params = new URLSearchParams(urlParts[1].split('#')[0]); // Handle hash fragments
        console.log('[ClientPortal] Used fallback URL parsing');
      }
    }
    
    // PRIORITY 1: Check for impersonation token directly in URL (most reliable)
    const impToken = params.get('imp_token');
    const impUserStr = params.get('imp_user');
    
    console.log('[ClientPortal] imp_token present:', !!impToken);
    console.log('[ClientPortal] imp_user present:', !!impUserStr);
    
    if (impToken && impUserStr) {
      console.log('[ClientPortal] 🔑 Impersonation token found in URL');
      
      try {
        const userData = JSON.parse(decodeURIComponent(impUserStr));
        
        // Set up the client session immediately
        localStorage.setItem('client_token', impToken);
        localStorage.setItem('client_user', JSON.stringify(userData));
        axios.defaults.headers.common['Authorization'] = `Bearer ${impToken}`;
        setClientUser(userData);
        
        console.log('[ClientPortal] ✅ Impersonation login successful! User:', userData.email);
        
        // Clean URL immediately (remove sensitive token from URL bar)
        // Use a slight delay to ensure state is set before URL change on mobile
        setTimeout(() => {
          window.history.replaceState({}, '', '/app');
        }, 100);
        
        setLoading(false);
        return;
      } catch (err) {
        console.error('[ClientPortal] ❌ Impersonation login error:', err);
        console.error('[ClientPortal] Error details:', err.message);
        console.error('[ClientPortal] imp_user raw value:', impUserStr);
      }
    }
    
    console.log('[ClientPortal] Checking for existing session...');
    
    // Normal token check
    const token = localStorage.getItem('client_token');
    const savedUser = localStorage.getItem('client_user');
    
    if (token && savedUser) {
      try {
        const user = JSON.parse(savedUser);
        if (user.role === 'client') {
          console.log('[ClientPortal] ✅ Existing session found:', user.email);
          
          // Validate token by making a test API call
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/meal-plans/me`)
            .then(() => {
              // Token is valid
              setClientUser(user);
              setLoading(false);
            })
            .catch((validationError) => {
              // Token is invalid/expired
              console.log('[ClientPortal] ⚠️ Session token expired or invalid, clearing...');
              localStorage.removeItem('client_token');
              localStorage.removeItem('client_user');
              delete axios.defaults.headers.common['Authorization'];
              setLoading(false);
            });
          
          return; // Don't call setLoading(false) here, wait for validation
        }
      } catch (err) {
        console.error('[ClientPortal] Error parsing saved user:', err);
      }
    } else {
      console.log('[ClientPortal] No existing session');
    }
    
    setLoading(false);
  }, []);

  const handleClientLogin = (userData, token) => {
    console.log('[App] handleClientLogin called with user:', userData?.email);
    console.log('[App] Token received:', token ? 'yes' : 'no');
    console.log('[App] Onboarding completed:', userData?.onboarding_completed);
    
    // Store in localStorage
    localStorage.setItem('client_token', token);
    localStorage.setItem('client_user', JSON.stringify(userData));
    
    // Verify storage worked
    const storedToken = localStorage.getItem('client_token');
    const storedUser = localStorage.getItem('client_user');
    console.log('[App] Token stored successfully:', !!storedToken);
    console.log('[App] User stored successfully:', !!storedUser);
    
    // Set axios header
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Set state to trigger re-render
    setClientUser(userData);
    console.log('[App] Client user state set, should be logged in now');
  };

  const handleClientLogout = () => {
    console.log('[App] handleClientLogout called');
    logActivity('logout');
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_user');
    delete axios.defaults.headers.common['Authorization'];
    setClientUser(null);
    console.log('[App] Client logged out, redirecting to login');
  };

  if (loading) {
    return <div className="loading-screen">Loading Client Portal...</div>;
  }

  return (
    <>
    <Routes>
      <Route path="login" element={!clientUser ? <ClientLogin onLogin={handleClientLogin} /> : <Navigate to="/app" />} />
      <Route path="signup" element={<Signup onLogin={handleClientLogin} />} />
      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route path="reset-password" element={<ResetPassword />} />
      <Route path="verify-email" element={<VerifyEmail />} />
      
      {/* Resubscribe page - accessible even without active subscription */}
      <Route path="resubscribe" element={clientUser ? <Resubscribe user={clientUser} /> : <Navigate to="/app/login" />} />
      
      {clientUser ? (
        <>
          {/* Navigation Persistence - tracks and restores last visited page */}
          <Route path="*" element={<NavigationPersistence isClient={true} />} />
          
          {/* Redirect to onboarding if not completed */}
          <Route path="/" element={
            <SubscriptionGuard user={clientUser}>
              {!clientUser.onboarding_completed
                ? <Navigate to="/app/onboarding" replace />
                : <ClientDashboardModern user={clientUser} onLogout={handleClientLogout} />}
            </SubscriptionGuard>
          } />
          <Route path="onboarding" element={<SubscriptionGuard user={clientUser}><OnboardingSwipeable user={clientUser} /></SubscriptionGuard>} />
          <Route path="orientation" element={<SubscriptionGuard user={clientUser}><OrientationIntro /></SubscriptionGuard>} />
          <Route path="meal-plan" element={<SubscriptionGuard user={clientUser}><MealPlanModern user={clientUser} onLogout={handleClientLogout} /></SubscriptionGuard>} />
          <Route path="checkin" element={<SubscriptionGuard user={clientUser}><WeeklyCheckin user={clientUser} onLogout={handleClientLogout} /></SubscriptionGuard>} />
          <Route path="weight-log" element={<SubscriptionGuard user={clientUser}><WeightLog user={clientUser} onLogout={handleClientLogout} /></SubscriptionGuard>} />
          <Route path="progress" element={<SubscriptionGuard user={clientUser}><ClientProgress user={clientUser} onLogout={handleClientLogout} /></SubscriptionGuard>} />
          <Route path="week-summary" element={<SubscriptionGuard user={clientUser}><WeekSummary user={clientUser} onLogout={handleClientLogout} /></SubscriptionGuard>} />
          <Route path="messages" element={<SubscriptionGuard user={clientUser}><Messages user={clientUser} onLogout={handleClientLogout} /></SubscriptionGuard>} />
          <Route path="profile" element={<SubscriptionGuard user={clientUser}><Profile user={clientUser} onLogout={handleClientLogout} /></SubscriptionGuard>} />
          <Route path="how-it-works" element={<SubscriptionGuard user={clientUser}><HowItWorks user={clientUser} onLogout={handleClientLogout} /></SubscriptionGuard>} />
          <Route path="*" element={<Navigate to="/app" />} />
        </>
      ) : (
        <Route path="*" element={<Navigate to="/app/login" />} />
      )}
    </Routes>
    {clientUser && <FloatingChatBubble />}
    </>
  );
}

// Main App Content with separate portals
function AppContent() {
  return (
    <div className="App">
      <Routes>
        {/* Public signup routes */}
        <Route path="/signup/:token" element={<ClientSignup />} />
        <Route path="/signup" element={<PublicSignup />} />
        <Route path="/signup-checkout" element={<SignupCheckout />} />
        <Route path="/signup-success" element={<SignupSuccess />} />
        
        {/* Public feedback page */}
        <Route path="/feedback" element={<FeedbackPage />} />
        
        {/* Legacy reset-password redirect (for old email links) */}
        <Route path="/reset-password" element={<Navigate to={`/app/reset-password${window.location.search}`} replace />} />
        
        {/* Coach Portal - accessible at /coach/* */}
        <Route path="/coach/*" element={<CoachPortal />} />
        
        {/* Public payment success route - verifies Stripe session BEFORE any auth redirect.
            Critical: must be top-level so unauthenticated clients aren't bounced to login. */}
        <Route path="/payment-success" element={<PaymentSuccess />} />
        
        {/* Client Portal - accessible at /app/* */}
        <Route path="/app/*" element={<ClientPortal />} />
        
        {/* Legal Pages */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/refund" element={<RefundPolicy />} />
        
        {/* Public Landing Page */}
        <Route path="/" element={<LandingPage />} />
        
        {/* Catch-all redirect */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
      <CookieConsentBanner />
    </BrowserRouter>
  );
}

export default App;
